import { flower, baseGrowSeconds, effectiveGrowSeconds, type FlowerId } from "./flowers";
import { quickBloomSpeed, type CompostBatch } from "./compost";

/**
 * PLANT GROWTH MODEL — the record a growing plant keeps, and the four pure functions that read it.
 *
 * Extracted verbatim from ui/App.tsx, where it sat above the component among the decor helpers and the
 * viewport hook. It is the one part of that file with no React in it at all: no state, no refs, no
 * closures — just a record and arithmetic over it. That made it the safest seam to pull first, and it
 * belongs in `sim/` with the rest of the model it depends on (flowers, compost) rather than in the view.
 *
 * THE IDEA WORTH KEEPING. Progress is BANKED, not recomputed. `grown` accumulates effective grow-seconds
 * each tick at whatever speed applied at that moment, so time SAVED by a bonus stays saved even if the
 * bonus later stops: a compost batch running out slows FUTURE progress, it never refunds what was earned.
 * Recomputing from `plantedAt` and a current speed — the obvious-looking simplification — silently
 * un-saves that time and is the bug this design exists to prevent. Legacy plantings with no `grown` fall
 * back to the old plantedAt formula so saves written before banking still load.
 *
 * Speed bonuses are ADDITIVE, not multiplied, and floored at 0.4× so growth can never stall.
 *
 * Callers own the moisture/night/light side of the bonus: `moistureBonusFor` in App composes moisture
 * stage and NIGHT_GROWTH_PENALTY (waived under a favoured grow light), then passes the total in. That split is
 * deliberate — those inputs need live component state, these functions must stay pure.
 */

/** A growing plant in a pot. `species` + `plantedAt` are the identity; the rest is banked progress. */
export interface PlantRec {
  species: FlowerId; plantedAt: number; compost?: CompostBatch;
  /** BANKED progress in effective grow-seconds (accumulates each tick at the current speed). Progress
   *  = grown / baseGrowSeconds. Banking it means time SAVED by a bonus is kept even if the bonus later
   *  stops — the plant never "un-saves" that time. */
  grown?: number;
  /** clock time (ms) `grown` was last advanced to. */
  lastAt?: number;
  /** GROW-LIGHT time accrued per colour (seconds spent under each colour AT NIGHT while lit). The colour
   *  with the most time is the plant's "dominant" light → its harvest effect. Banked each tick; drift-only
   *  so it's stripped from the save change-key (persisted on real changes). */
  litBy?: Record<string, number>;
  /** the shelf tier of the light this plant has spent the most NIGHT-time under (for the dominant effect
   *  strength). Tracked alongside litBy. */
  litTier?: number;
  /** grow-seconds GIFTED at sowing — the tutorial's first crop (useGreenhouse, FIRST_CROP_HEADSTART_SECONDS), banked
   *  so it ripens in about a minute. Only the PICTURE reads it (`shownProgressOf`): the plant is drawn from the seed
   *  over the time that is left, instead of appearing three-quarters grown the moment it is sown. */
  giftedGrown?: number;
}

/** Every plant grows this much SLOWER at night (additive to the speed multiplier) — except a plant under its
 *  favoured grow light, which keeps full speed (growLight.ts). The light no longer adds speed of its own. */
export const NIGHT_GROWTH_PENALTY = 0.20;

/** The growth-speed bonus a compost batch grants — now ENTIRELY from its Quick Bloom effect (quality/star
 *  grade was removed). The rolled quick-bloom % is a guaranteed additive growth-speed bump. */
export function compostSpeedBonus(b?: CompostBatch): number {
  return quickBloomSpeed(b);
}

/** Combined growth-speed multiplier for a planting: COMPOST bonus (its Quick Bloom effect) + MOISTURE
 *  bonus, ADDITIVELY (not multiplied), floored so it never stalls. */
export function speedMultFor(rec: PlantRec, moistureBonus: number): number {
  return Math.max(0.4, 1 + compostSpeedBonus(rec.compost) + moistureBonus);   // additive; never below 0.4×
}

/**
 * THE CEILING `speedMultFor` CAN REACH — the fastest any plant can legitimately grow, and the number the
 * SERVER clamps banked progress against (server/index.js, reconcilePlantings). Every additive term at its
 * simultaneous best:
 *
 *   1.00  base
 * + 0.47  Growth Speed compost, LEGACY peak. A compost the scrap composter rolls today tops out at +0.40
 *         (a ★5's top band of 40 in composter.ts RANK_BANDS × the quickBloom EFFECT_WEIGHT 1.0). The retired
 *         skill-point composter could reach 47% (a 20% ceiling, ×1.2 tier headroom, +22.5 from the skill
 *         budget), and save.ts keeps an old batch's amount verbatim, so the bound still allows 0.47.
 * + 0.30  `damp` soil (moisture.ts `moistureSpeedBonus`) — the best stage there is.
 * + 0.55  a preferred-colour grow light at max tier — RETIRED (Sep 2026): a favoured grow light now only
 *         lifts the night penalty below, so this term is 0 and the reachable peak is 1.70 (1.77 with a
 *         legacy 47% batch).
 * − 0.20  NIGHT_GROWTH_PENALTY — which that old light only worked alongside.
 * = 2.12
 *
 * THE CEILING IS LEFT AT 2.12 ON PURPOSE. It is an anti-cheat bound shared with the server, and a player
 * still running a tab from before the light change grows under the old +0.55; lowering it would clamp them.
 * Tighten it (here AND server/index.js) once no old client can be live.
 *
 * It is a BOUND, not a speed players are expected to see: a legacy 47% Growth Speed batch on damp soil was,
 * with the retired maxed preferred light, the only way there. A typical boosted plant runs
 * ~1.3–1.6×. This — not `GROWTH_SPEED_FLOOR` (0.4 = 2.5×, sim/flowers.ts), which is looser and never
 * binds — is the fastest-growth figure BALANCE.md §1 quotes.
 *
 * IF YOU RETUNE ANY OF THOSE TERMS, RETUNE THIS. `server/save.test.js` re-derives this number from the sim
 * sources and fails if the server's mirrored copy drifts, but nothing can check the arithmetic above
 * against a balance change you make on purpose — raise this and the server stops clamping honest players;
 * leave it low and a legitimately fast greenhouse gets its progress trimmed.
 */
export const MAX_SPEED_MULT = 2.12;

/**
 * Progress 0..1 from BANKED grow-time. Progress is accumulated (`rec.grown`, in effective grow-seconds)
 * rather than recomputed from plantedAt — so time SAVED by a bonus is kept even if the bonus later
 * stops (a bonus that turns off just slows FUTURE progress; it never refunds the saved time). This
 * reads the banked amount plus a live extrapolation to `now` at the current speed (the per-frame tick
 * commits the banked value). Legacy plantings with no `grown` fall back to the old plantedAt formula.
 */
export function progressOf(rec: PlantRec, now: number, moistureBonus = 0): number {
  const base = baseGrowSeconds(flower(rec.species));
  if (rec.grown === undefined || rec.lastAt === undefined) {
    // migration / not-yet-banked: seed from plantedAt at the current speed.
    const secs = effectiveGrowSeconds(flower(rec.species), speedMultFor(rec, moistureBonus), true);
    return Math.max(0, Math.min(1, (now - rec.plantedAt) / 1000 / secs));
  }
  const extra = Math.max(0, (now - rec.lastAt) / 1000) * speedMultFor(rec, moistureBonus);
  return Math.max(0, Math.min(1, (rec.grown + extra) / base));
}

/** The progress to DRAW: `progressOf`, with any gifted head start (`giftedGrown`) taken off the front so a gifted
 *  plant still starts as a seed and grows through every stage. Ripeness, timers and the harvest read `progressOf`. */
export function shownProgressOf(rec: PlantRec, now: number, moistureBonus = 0): number {
  const p = progressOf(rec, now, moistureBonus);
  const g = rec.giftedGrown ?? 0;
  if (!(g > 0)) return p;
  const f = g / baseGrowSeconds(flower(rec.species));
  return f >= 1 ? p : Math.max(0, Math.min(1, (p - f) / (1 - f)));
}

/** COMMIT banked progress up to `now` at the given moisture bonus (called each tick per planting). */
export function bankProgress(rec: PlantRec, now: number, moistureBonus: number): void {
  if (rec.grown === undefined || rec.lastAt === undefined) { rec.grown = Math.max(0, (now - rec.plantedAt) / 1000) * speedMultFor(rec, moistureBonus); rec.lastAt = now; return; }
  rec.grown += Math.max(0, (now - rec.lastAt) / 1000) * speedMultFor(rec, moistureBonus);
  rec.lastAt = now;
}

/**
 * THE AWAY CAP — the most absence the greenhouse simulation will credit. Growth, soil drying, the thirst
 * clock and the grid meter all run for the first four hours the player is gone and then STOP; the rest of
 * the gap is skipped, as if the greenhouse were paused until they came back (useGreenhouse `tickMoisture`
 * runs the sim up to the cap, then shifts every clock stamp forward over the remainder).
 *
 * The SERVER mirrors this in `reconcilePlantings` (server/index.js `AWAY_CAP_MS`): a PUT's `grown` budget
 * is at most AWAY_CAP_HOURS at MAX_SPEED_MULT, plus grace, so a crafted save cannot bank more than the
 * client could. `server/save.test.js` reads this line and fails if the two drift — change them together.
 */
export const AWAY_CAP_HOURS = 4;
export const AWAY_CAP_MS = AWAY_CAP_HOURS * 3_600_000;

/** Width of one banking slice. A 1 s tick is one slice; a four-hour catch-up is 48. Five minutes keeps the
 *  error at a soil-stage or day/night boundary under a few seconds of growth. */
export const BANK_SLICE_MS = 5 * 60_000;

/**
 * COMMIT banked progress up to `now` IN SLICES, sampling the additive bonus at each slice's midpoint. This
 * is the live banking path; `bankProgress` above is the one-multiply version it replaced for spans.
 *
 * WHY SLICES. After a reload the first tick banks the whole absence at once, and one multiply charged all
 * of it at the speed of the instant the player returned: a pot that dried out an hour before they came
 * back banked the entire gap at the dry 0.6×, and returning at night under a preferred light banked eight
 * hours of night boost (and 28,800 s of `litBy`) for an absence that was mostly daytime. Moisture drain and
 * day/night are both computable for any past timestamp, so the gap is walked in BANK_SLICE_MS steps instead.
 *
 * `bonusAt(t, dtSec)` returns the additive speed bonus at time `t` for a slice `dtSec` long; it is also
 * where the caller tallies per-slice side records (the grow-light seconds in `litBy`), so those are sampled
 * at exactly the same instants as the growth. Legacy records with no `grown` keep the old seeding.
 */
export function bankProgressSliced(rec: PlantRec, now: number, bonusAt: (t: number, dtSec: number) => number): void {
  if (rec.grown === undefined || rec.lastAt === undefined) { bankProgress(rec, now, bonusAt(now, 0)); return; }
  let grown = rec.grown;
  for (let t = rec.lastAt; t < now;) {
    const end = Math.min(now, t + BANK_SLICE_MS);
    const dtSec = (end - t) / 1000;
    grown += dtSec * speedMultFor(rec, bonusAt((t + end) / 2, dtSec));
    t = end;
  }
  rec.grown = grown;
  rec.lastAt = Math.max(rec.lastAt, now);
}
